export interface TimelineStep {
  id: number;
  title: string;
  description: string;
  date: string;
  isActive?: boolean;
}

export interface Milestone {
  icon: string;
  text: string;
}

export interface Step {
  number: number;
  title: string;
  description: string;
  icon?: string;
}

export interface RewardSection {
  title: string;
  description: string;
  milestones: Milestone[];
  steps?: Step[];
}

export const SCHOLARSHIP_TIMELINE_STEPS: TimelineStep[] = [
  {
    id: 1,
    title: "Application Start",
    description:
      "Don't wait a second more, jump on that application bandwagon and show us what you've got!",
    date: "24th Jan 2026",
    isActive: true,
  },
  {
    id: 2,
    title: "Application Deadline",
    description:
      "Time's ticking! Submit your application soon and give yourself a well-deserved high five!",
    date: "31st Jan 2027",
  },
  {
    id: 3,
    title: "Shortlisting",
    description: "Drumroll, please! Keep your phone close. We'll contact you if you're selected!",
    date: "20th Feb 2026",
  },
  {
    id: 4,
    title: "Winner Announcement",
    description:
      "Winners will be announced on 27 Feb 2026. Stay tuned to see if you've earned your spot!",
    date: "27th Feb 2026",
  },
];

export const SCHOLARSHIP_REWARD_SECTIONS: RewardSection[] = [
  {
    title: "Referral Rewards – Bring Friends, Earn Points!",
    description:
      "Level up with your squad! Invite friends in the UK, USA, Canada or Ireland and each referral earns you 5 points. And there's more to come!",
    milestones: [
      { icon: "♦", text: "Get 5 Referral: Earn direct access to the jury's inbox" },
      { icon: "♦", text: "10 Referrals: We'll double your total points" },
      { icon: "♦", text: "20 Referrals: Unlock winning SOPs from past winners" },
    ],
    steps: [
      {
        number: 1,
        title: "Tap the link",
        description:
          "Click on the referral link provided in your email to begin the referral process.",
      },
      {
        number: 2,
        title: "Copy the URL",
        description: "Once the referral page opens, copy the full URL to share with your network.",
      },
      {
        number: 3,
        title: "Spread the word",
        description: "Share it with friends on WhatsApp, Insta, Snapchat and wherever they are!",
      },
    ],
  },
  {
    title: "UGC Bonus – Post, Tag, And Win!",
    description:
      "Flex your creativity and earn points! Share the image/video, tag us, and score +5 points per post on LinkedIn or any platform you love!",
    milestones: [
      { icon: "♦", text: "Every valid post = 5 bonus points" },
      { icon: "♦", text: "No limits: Post more, earn more" },
      { icon: "♦", text: "Combine UGC + referrals = major point boost" },
    ],
    steps: [
      {
        number: 1,
        title: "Download the image or video provided",
        description: "",
        icon: "download",
      },
      {
        number: 2,
        title: "Post it on your social media and tag @amber in the caption",
        description: "",
        icon: "post",
      },
      {
        number: 3,
        title: "Earn points as your post gets noticed!",
        description: "",
        icon: "earn_pts",
      },
    ],
  },
];

export const SCHOLARSHIP_TIMELINE_CONFIG = {
  title: "Dates To Remember",
  subtitle: "Don't miss out on this exciting opportunity - Apply Today!",
};

export interface Winner {
  name: string;
  position: number;
  amount: string;
}

export interface Video {
  id: string;
  title: string;
}

export interface EditionData {
  edition: string;
  totalAmount: string;
  winners: Winner[];
  videos: Video[];
}

export const getScholarshipEditionsData = (t: (key: string) => string): EditionData[] => [
  {
    edition: t("scholarship.editions.first.edition"),
    totalAmount: t("scholarship.editions.first.totalamount"),
    winners: [
      {
        name: t("scholarship.editions.first.winner1.name"),
        position: 1,
        amount: t("scholarship.editions.first.winner1.amount"),
      },
      {
        name: t("scholarship.editions.first.winner2.name"),
        position: 2,
        amount: t("scholarship.editions.first.winner2.amount"),
      },
      {
        name: t("scholarship.editions.first.winner3.name"),
        position: 3,
        amount: t("scholarship.editions.first.winner3.amount"),
      },
    ],
    videos: [
      { id: "AtvJKVALGLQ", title: t("scholarship.editions.first.video1.title") },
      { id: "uIGljzTaFNs", title: t("scholarship.editions.first.video2.title") },
      { id: "cvWk5ENEnb8", title: t("scholarship.editions.first.video3.title") },
    ],
  },
  {
    edition: t("scholarship.editions.second.edition"),
    totalAmount: t("scholarship.editions.second.totalamount"),
    winners: [
      {
        name: t("scholarship.editions.second.winner1.name"),
        position: 1,
        amount: t("scholarship.editions.second.winner1.amount"),
      },
      {
        name: t("scholarship.editions.second.winner2.name"),
        position: 2,
        amount: t("scholarship.editions.second.winner2.amount"),
      },
      {
        name: t("scholarship.editions.second.winner3.name"),
        position: 3,
        amount: t("scholarship.editions.second.winner3.amount"),
      },
    ],
    videos: [
      { id: "BTowsSvUXqw", title: t("scholarship.editions.second.video1.title") },
      { id: "Rx6Wta61dOs", title: t("scholarship.editions.second.video2.title") },
      { id: "XXplllz7opA", title: t("scholarship.editions.second.video3.title") },
    ],
  },
  {
    edition: t("scholarship.editions.third.edition"),
    totalAmount: t("scholarship.editions.third.totalamount"),
    winners: [
      {
        name: t("scholarship.editions.third.winner1.name"),
        position: 1,
        amount: t("scholarship.editions.third.winner1.amount"),
      },
      {
        name: t("scholarship.editions.third.winner2.name"),
        position: 2,
        amount: t("scholarship.editions.third.winner2.amount"),
      },
      {
        name: t("scholarship.editions.third.winner3.name"),
        position: 3,
        amount: t("scholarship.editions.third.winner3.amount"),
      },
    ],
    videos: [
      { id: "qtOgXqSpCOQ", title: t("scholarship.editions.third.video1.title") },
      { id: "uPivRC-AyDc", title: t("scholarship.editions.third.video2.title") },
      { id: "8U_IdOyubFY", title: t("scholarship.editions.third.video3.title") },
    ],
  },
  {
    edition: t("scholarship.editions.fourth.edition"),
    totalAmount: t("scholarship.editions.fourth.totalamount"),
    winners: [
      {
        name: t("scholarship.editions.fourth.winner1.name"),
        position: 1,
        amount: t("scholarship.editions.fourth.winner1.amount"),
      },
      {
        name: t("scholarship.editions.fourth.winner2.name"),
        position: 2,
        amount: t("scholarship.editions.fourth.winner2.amount"),
      },
      {
        name: t("scholarship.editions.fourth.winner3.name"),
        position: 3,
        amount: t("scholarship.editions.fourth.winner3.amount"),
      },
    ],
    videos: [
      { id: "yBT7RZa25sA", title: t("scholarship.editions.fourth.video1.title") },
      { id: "QFeaP6yQy1s", title: t("scholarship.editions.fourth.video2.title") },
      { id: "AZLpzEl--E4", title: t("scholarship.editions.fourth.video3.title") },
    ],
  },
];

export interface FAQItem {
  question: string;
  answer: string;
}

export const getScholarshipFaqData = (t: (key: string) => string): FAQItem[] => [
  {
    question: t("scholarship.faq.q1.question"),
    answer: t("scholarship.faq.q1.answer"),
  },
  {
    question: t("scholarship.faq.q3.question"),
    answer: t("scholarship.faq.q3.answer"),
  },
  {
    question: t("scholarship.faq.q4.question"),
    answer: t("scholarship.faq.q4.answer"),
  },
  {
    question: t("scholarship.faq.q5.question"),
    answer: t("scholarship.faq.q5.answer"),
  },
  {
    question: t("scholarship.faq.q6.question"),
    answer: t("scholarship.faq.q6.answer"),
  },
  {
    question: t("scholarship.faq.q7.question"),
    answer: t("scholarship.faq.q7.answer"),
  },
  {
    question: t("scholarship.faq.q8.question"),
    answer: t("scholarship.faq.q8.answer"),
  },
  {
    question: t("scholarship.faq.q9.question"),
    answer: t("scholarship.faq.q9.answer"),
  },
  {
    question: t("scholarship.faq.q10.question"),
    answer: t("scholarship.faq.q10.answer"),
  },
];
